<script setup>
import {computed, onMounted, ref, watch} from "vue";
import PagerContainer from "./PagerContainer.vue";
import ListContainer from "./ListContainer.vue";
import {ClientJS} from "clientjs";

const siteUrl = document.querySelector('meta[name="site-url"]').content;
const SORT_TYPE_LABELS = {
  id: '新着順',
  empathy: '共感順',
  comment: 'コメント順',
};

// ソートの選択肢を表示するかどうか
const isShowSortOptions = ref(false);

// 現在選択されているソートの種類
const sortType = ref('id');

// 現在選択されているソートの種類から名称を決定する
const sortTypeLabel = computed(() => SORT_TYPE_LABELS[sortType.value]);

// 現在のページ番号
const currentPage = ref(1);

// 検索結果の総数
const total = ref(0);

// 広告一覧
const ads = ref(['', '', '', '', '', '', '']);

// 描画する投稿一覧
const data = ref([]);

// 現在スマホで閲覧されているかどうか
const isSp = computed(() => window.innerWidth <= 835);

// 実際に項目として描画するデータ
// 基本は data を描画として用いるが、data 3 つにつき一回 ads を挟む
const lists = computed(() => {
  // 結果配列
  const result = [];

  // data を 3 つずつに分割して ads を挟む
  for (let i = 0; i < data.value.length; i++) {
    // id を追加
    result.push(data.value[i]);

    // 3 つごとに ads を追加
    if (i % 3 === 2) {
      result.push(ads.value[Math.floor(i / 3) % ads.value.length]);
    }
  }

  // 返す
  return result;
});

// 広告の同期
const syncAds = async () => {
  try {
    // 取得を試みる
    const response = await fetch(siteUrl+'place/get-post-ads');

    // JSON パースを試みる
    const data = await response.json();

    // 追加
    ads.value = data;
  }

    // 特に何もしない(例外で処理を中断するために存在)
  catch (e) {
    console.error(e);
  }
};

// データの同期
const syncData = async (page = null) => {
  try {
    // 取得を試みる
    const response = await fetch(siteUrl+'place/get-posts?page='+(page === null ? currentPage.value : page)+'&number='+(isSp.value ? '18' : '20')+'&sort='+sortType.value+'&fingerprint='+(new ClientJS()).getFingerprint());

    // JSON パースを試みる
    const parsed = await response.json();

    // データをセット
    data.value = page === null ? data.value.concat(parsed.data) : parsed.data;
    total.value = parsed.total;

    // page = null の場合は無限スクロール用として、次回の currentPage をインクリメントする
    if (page === null) {
      currentPage.value++;
    }

    // そうでない場合は page をセット
    else {
      currentPage.value = page;
    }

    // 成功を返す
    return true;
  }

    // 特に何もしない(例外で処理を中断するために存在)
  catch (e) {
    console.error(e);
  }

  // 失敗を返す
  return false;
};

// ページャーによるページ移動
const moveByPager = async (page) => {
  // データの書き換え
  const result = await syncData(page);

  // スクロール
  if (result) {
    const target = document.getElementById('post-button-wrapper');
    if (target) {
      target.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }
};

// ソートオプションを開く
const openSortOptions = () => {
  isShowSortOptions.value = true;
};

// 初期データの同期
syncAds();
syncData(isSp.value ? null : 1);

// sortType の変更を監視
watch(sortType, () => {
  // 初期化
  currentPage.data = 1;
  data.value = [];

  // データを同期
  syncData(1);
});

// 無限スクロール実装
onMounted(() => {
  // 連続でスクロールイベントが発生するのを防ぐためのタイマー
  let timer = 0;

  window.addEventListener('scroll', function() {
    // 前回のタイマーをクリア
    clearTimeout(timer);

    // 200ms 後に処理を実行
    timer = setTimeout(function() {
      // 現在スマホでなければ何もしない
      if ( ! isSp.value) {
        return;
      }

      // ページの高さ
      const documentHeight = document.documentElement.scrollHeight;

      // スクロールされた高さ + ビューポートの高さ
      const scrollPosition = window.scrollY + window.innerHeight;

      // スクロール位置がページの一番下に到達したかを確認
      if (scrollPosition >= documentHeight) {
        syncData(null);
      }
    }, 200);
  });

  // 疑似セレクトボックス外の要素をクリックした際、疑似セレクトボックスを非表示にする、
  document.addEventListener('click', (event) => {
    if ( ! event.target.closest('.modSelectTrigger')) {
      isShowSortOptions.value = false;
    }
  });
});
</script>

<template>
  <div class="gbMainInner">
    <div class="lyHorizontalEnd">
      <p class="lyMr4 lyTbMr8">
        <a
          :href="siteUrl+'place/list?filter=wanted'"
          class="lyBlock lyPt8 lyPb8 lyPr16 lyPl16 pFont14 pFontMedium pFontBlack pBackColorWhite pBorderRadiusEllipse pHoverBackBlack10"
        >回答募集</a>
      </p>
      <p class="lyMr4 lyTbMr8">
        <a
          :href="siteUrl+'place/list?filter=campaign'"
          class="lyBlock lyPt8 lyPb8 lyPr16 lyPl16 pFont14 pFontMedium pFontBlack pBackColorWhite pBorderRadiusEllipse pHoverBackBlack10"
        >キャンペーン</a>
      </p>
      <div :class="{modSelectTrigger: true, pageSortTriggerLayered: true, isShow: isShowSortOptions.value}">
        <button
          type="button"
          id="child01"
          class="lyHvCnt lyPt8 lyPb8 lyPr16 lyPl16 pFont14 pFontMedium pFontBlack pBackColorWhite pBorderRadiusEllipse pHoverBackBlack10"
          @click="openSortOptions"
        >
          {{sortTypeLabel}}
          <span class="lyBlock lyW16px lyMl8">
									<img
                    :src="siteUrl+'images/iconSort.svg'"
                    width="16"
                    height="16"
                    alt="">
								</span>
        </button>
        <div class="modSelectListContainer mdRight">
          <ul class="modSelectList">
            <li class="pBorderBottom1pxSolidGray4">
              <label class="lyW100 lyPt12 lyPb12 lyPr8 lyPl8 lyTextCnt pFontBold pHoverBackGray4 pAnimation pCursorPointer">
                <input
                  v-model="sortType"
                  type="radio"
                  name="sort"
                  value="id"
                  >
                新着順
              </label>
            </li>
            <li class="pBorderBottom1pxSolidGray4">
              <label class="lyW100 lyPt12 lyPb12 lyPr8 lyPl8 lyTextCnt pFontBold pHoverBackGray4 pAnimation pCursorPointer">
                <input
                  v-model="sortType"
                  type="radio"
                  name="sort"
                  value="empathy"
                >
                共感順
              </label>
            </li>
            <li>
              <label class="lyW100 lyPt12 lyPb12 lyPr8 lyPl8 lyTextCnt pFontBold pHoverBackGray4 pAnimation pCursorPointer">
                <input
                  v-model="sortType"
                  type="radio"
                  name="sort"
                  value="comment"
                >
                コメント順
              </label>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
  <div class="gbMainInner">
    <ListContainer
      v-for="(list, index) in lists"
      :key="index"
      :index="index"
      :data="list"
    />
  </div>
  <div class="gbMainInner">
    <PagerContainer
      :total="total"
      :current-page="currentPage"
      @move-paginate="moveByPager($event)"
    />
  </div>
</template>
